/**
 * SO Location Selector Component
 *
 * Displays location selection UI for Sales Order products
 * - Single location: Inline display with freshness and PO info
 * - Multiple locations: Button to open LocationSelectorDialog
 */

import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { MapPin, ChevronDown } from 'lucide-react';
import InventoryFreshnessIndicator from './InventoryFreshnessIndicator';
import POSourceChip from './POSourceChip';
import LocationSelectorDialog from './LocationSelectorDialog';

function SOLocationSelector({ product, onLocationChange, groupedInventory }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!product) return null;

  const {
    product_code,
    selectedLocation,
    requiresLocationSelection,
    locationsWithStock = 0,
    locations = [],
    available_quantity,
    unit = '支'
  } = product;

  // Get grouped inventory data for this product
  const inventoryData = groupedInventory?.[product_code];

  // Get current location details
  const currentLocationData = locations.find(loc => loc.location === selectedLocation);

  // Handle location change from dialog
  const handleLocationSelect = (selectedLoc) => {
    if (onLocationChange && selectedLoc) {
      onLocationChange(product, selectedLoc.location);
    }
    setDialogOpen(false);
  };

  // Single location (auto-selected) - Simple display
  if (!requiresLocationSelection || locationsWithStock === 1) {
    return (
      <div className="flex flex-col gap-1">
        {/* Location Name */}
        <div className="flex items-center gap-1">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {selectedLocation}
          </span>
        </div>

        {/* Available Quantity */}
        <span className="text-xs text-muted-foreground">
          {available_quantity}{unit} 可用
        </span>

        {/* Freshness Indicator */}
        {currentLocationData?.last_movement && (
          <InventoryFreshnessIndicator
            timestamp={currentLocationData.last_movement.movement_date}
            variant="inline"
            size="small"
          />
        )}

        {/* PO Source (first one) */}
        {currentLocationData?.source_origins?.[0] && (
          <div className="mt-1">
            <POSourceChip
              source={currentLocationData.source_origins[0]}
              size="small"
              showQuantity={false}
            />
          </div>
        )}
      </div>
    );
  }

  // Multiple locations - Show selector button
  return (
    <div className="flex flex-col gap-1">
      {/* Location Selector Button */}
      <div className="relative inline-block">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDialogOpen(true)}
          className="justify-between min-w-[180px]"
        >
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <div className="flex flex-col items-start">
              <span className="text-sm font-medium">
                {selectedLocation}
              </span>
              <span className="text-xs text-muted-foreground">
                {available_quantity}{unit} 可用
              </span>
            </div>
          </div>
          <ChevronDown className="h-4 w-4" />
        </Button>
        <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0">
          {locationsWithStock}
        </Badge>
      </div>

      {/* Quick Info for Selected Location */}
      <div className="flex gap-1 flex-wrap mt-1">
        {currentLocationData?.data_freshness && (
          <Badge
            variant={
              currentLocationData.data_freshness === 'fresh' ? 'default' :
              currentLocationData.data_freshness === 'recent' ? 'secondary' : 'outline'
            }
            className={`h-5 text-[0.7rem] ${
              currentLocationData.data_freshness === 'fresh' ? 'bg-success/100' : ''
            }`}
          >
            {currentLocationData.data_freshness === 'fresh' ? '新鲜' :
             currentLocationData.data_freshness === 'recent' ? '近期' : '过期'}
          </Badge>
        )}

        {currentLocationData?.source_origins?.[0] && (
          <POSourceChip
            source={currentLocationData.source_origins[0]}
            size="small"
            showQuantity={false}
          />
        )}
      </div>

      {/* Location Selector Dialog */}
      {inventoryData && (
        <LocationSelectorDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          product={inventoryData}
          onSelect={handleLocationSelect}
          showOnlyWithStock={true}
        />
      )}
    </div>
  );
}

export default SOLocationSelector;
